/**
 * MARK:- Shipping Restrictions Feature
 * Client-side logic for displaying shipping restriction notices on PDP
 *
 * This script handles:
 * 1. Location detection waterfall (localStorage > customer address > Shopify Privacy API)
 * 2. Shipping status determination based on product metafields
 * 3. Dynamic UI updates when zone changes
 * 4. Optional Add-to-Cart button disabling for restricted states
 *
 * LOCATION PRIORITY:
 * 1. Growing Zone localStorage (explicit user selection - highest priority)
 * 2. Customer default address (logged-in users)
 * 3. Shopify Customer Privacy API (geo-detection fallback)
 *
 * DEPENDENCIES:
 * - grow-zone-finder.js (for zone:updated event and toggleGrowZoneDrawer function)
 *
 * CUSTOM EVENT:
 *   Event Name: 'shipping:restriction-checked'
 *   Event Detail: { status: string, stateCode: string|null, source: string, timestamp: string }
 *   Fired when: Restriction status is determined
 */

(function() {
  'use strict';

  // MARK:- Prevent multiple initializations
  if (window.shippingRestrictionsInitialized) {
    return;
  }
  window.shippingRestrictionsInitialized = true;

  /**
   * MARK:- Main initialization - handle both DOM ready and already-loaded cases
   * Script may load after DOMContentLoaded has fired, so check readyState
   */
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initShippingRestrictions);
  } else {
    // DOM already ready - initialize immediately
    initShippingRestrictions();
  }

  /**
   * MARK:- Initialize shipping restrictions for all notice containers on the page
   */
  function initShippingRestrictions() {
    const containers = document.querySelectorAll('.shipping-restriction-notice-container');

    if (containers.length === 0) {
      return;
    }

    containers.forEach(container => {
      initContainer(container);
    });

    // MARK:- Listen for zone updates from grow-zone-finder.js
    document.addEventListener('zone:updated', handleZoneUpdate);

    // MARK:- #2899: re-judge on the persisted shopper zone (custom-zone-verdict.js), so consecutive ZIP changes work without a reload
    document.addEventListener('nhzone:changed', () => {
      document.querySelectorAll('.shipping-restriction-notice-container').forEach(applyZoneVerdict);
    });
  }

  /**
   * MARK:- Initialize a single shipping restriction container
   * @param {HTMLElement} container - The container element
   */
  function initContainer(container) {
    // MARK:- Parse restriction data from data attributes
    const restrictedStates = parseJsonAttribute(container.dataset.restrictedStates);
    const weatherStates = parseJsonAttribute(container.dataset.weatherStates);
    const disableAtc = container.dataset.disableAtc === 'true';
    const serverStatus = container.dataset.serverStatus;
    const cartState = container.dataset.cartState;
    const customerState = container.dataset.customerState;
    const customerCountry = container.dataset.customerCountry;

    // MARK:- Parse custom button selectors for third-party apps
    const customSelectors = container.dataset.customSelectors || '';

    // MARK:- Parse zone protection config (#2899: plant facts for NHZone.judge)
    const enableZoneProtection = container.dataset.enableZoneProtection === 'true';
    const addZoneProperty = container.dataset.addZoneProperty === 'true';
    const zoneFacts = {
      zoneRange: container.dataset.zoneRange || '',
      zoneList: parseJsonAttribute(container.dataset.zoneList),
      productType: container.dataset.productType || '',
      tags: parseJsonAttribute(container.dataset.productTags)
    };
    let zoneUrls = {};
    try {
      zoneUrls = JSON.parse(container.dataset.zoneUrls || '{}');
    } catch (error) {
      zoneUrls = {};
    }

    // MARK:- Store config on container for later use
    container._shippingConfig = {
      restrictedStates,
      weatherStates,
      disableAtc,
      customSelectors
    };

    // MARK:- Store zone protection config separately
    container._zoneProtectionConfig = {
      zoneFacts,
      zoneUrls,
      enableZoneProtection,
      addZoneProperty
    };

    // MARK:- If server already determined status, handle buttons and dispatch event
    if (serverStatus !== 'unknown' && cartState) {
      // MARK:- Handle ATC button for server-determined status
      if (disableAtc) {
        handleAtcButton(container, serverStatus);
      }
      dispatchRestrictionEvent(serverStatus, cartState, 'server');

      // MARK:- Handle zone protection (runs after state check)
      applyZoneVerdict(container);
      return;
    }

    // MARK:- Try to determine state from client-side sources
    const locationData = detectLocation(customerState, customerCountry);

    if (locationData.stateCode) {
      updateRestrictionStatus(container, locationData.stateCode, locationData.source);
    }
    // else: Stay in "unknown" state, prompting user to enter ZIP

    // MARK:- Check zone protection from client-side sources
    applyZoneVerdict(container);
  }

  /**
   * MARK:- Location detection waterfall
   * Priority: localStorage > Storefront API fetch > customer address > Shopify Privacy API
   * @param {string} customerState - Customer's default address state (from server)
   * @param {string} customerCountry - Customer's default address country (from server)
   * @returns {object} - { stateCode: string|null, source: string }
   */
  function detectLocation(customerState, customerCountry) {
    // MARK:- Priority 1: localStorage state (explicit user selection via Growing Zone)
    const localState = localStorage.getItem('growing_state');
    if (localState) {
      return { stateCode: localState, source: 'localStorage' };
    }

    // MARK:- Priority 1.5: If we have zipcode but no state, fetch from Storefront API
    // This handles cases where zone was set before state support was added
    const localZip = localStorage.getItem('growing_zipcode');
    if (localZip) {
      fetchStateFromStorefront(localZip);
    }

    // MARK:- Priority 2: Logged-in customer's default address (if US)
    if (customerState && customerCountry === 'US') {
      return { stateCode: customerState, source: 'customerAddress' };
    }

    // MARK:- Priority 3: Shopify Customer Privacy API (async, handled separately)
    // Only attempt if the API is available and consented
    tryPrivacyApiDetection();

    // Return null for now - async fetches will update UI when complete
    return { stateCode: null, source: 'none' };
  }

  /**
   * MARK:- Fetch state code from Storefront API using saved zipcode
   * Uses the zipcode as metaobject handle to get state_code field
   * This is a fallback for when zone was set before state support was added
   * @param {string} zipCode - The ZIP code (metaobject handle)
   */
  async function fetchStateFromStorefront(zipCode) {
    // MARK:- Get Storefront API settings from window.zoneThemeSettings
    const settings = window.zoneThemeSettings;
    if (!settings?.storefrontApiToken || !settings?.shopDomain) {
      return;
    }

    const query = `{
      metaobject(handle: { handle: "${zipCode}", type: "zip_lookup" }) {
        stateCode: field(key: "state_code") {
          value
        }
      }
    }`;

    try {
      const response = await fetch(`https://${settings.shopDomain}/api/2025-10/graphql.json`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Shopify-Storefront-Access-Token': settings.storefrontApiToken
        },
        body: JSON.stringify({ query })
      });

      const data = await response.json();
      const stateCode = data?.data?.metaobject?.stateCode?.value;

      if (stateCode) {
        // MARK:- Store in localStorage for future use
        localStorage.setItem('growing_state', stateCode);

        // MARK:- Update all shipping restriction containers
        const containers = document.querySelectorAll('.shipping-restriction-notice-container');
        containers.forEach(container => {
          updateRestrictionStatus(container, stateCode, 'storefrontFetch');
        });
      }
    } catch (error) {
      // Silently fail - other fallbacks will be used
    }
  }

  /**
   * MARK:- Attempt to get region from Shopify Customer Privacy API
   * This is async and will update the UI if successful
   */
  function tryPrivacyApiDetection() {
    // MARK:- Check if Shopify Customer Privacy API is available
    if (typeof Shopify === 'undefined' || typeof Shopify.customerPrivacy === 'undefined') {
      return;
    }

    // MARK:- Wait for privacy consent to be determined
    const checkPrivacy = () => {
      if (Shopify.customerPrivacy.getRegion) {
        try {
          const region = Shopify.customerPrivacy.getRegion();
          if (region && region.countryCode === 'US' && region.provinceCode) {
            // MARK:- Found US state via Privacy API
            const containers = document.querySelectorAll('.shipping-restriction-notice-container');
            containers.forEach(container => {
              updateRestrictionStatus(container, region.provinceCode, 'privacyApi');
            });
          }
        } catch (error) {
          // Privacy API not consented or error - stay in unknown state
        }
      }
    };

    // MARK:- Check immediately and also after a short delay (API may not be ready)
    checkPrivacy();
    setTimeout(checkPrivacy, 500);
  }

  /**
   * MARK:- Handle zone update events from grow-zone-finder.js
   * @param {CustomEvent} event - The zone:updated event
   */
  function handleZoneUpdate(event) {
    const { stateCode } = event.detail;

    // MARK:- Update all containers on the page
    const containers = document.querySelectorAll('.shipping-restriction-notice-container');
    containers.forEach(container => {
      if (stateCode) {
        updateRestrictionStatus(container, stateCode, 'zoneUpdate');
      } else {
        // No state available (legacy API) - show unknown status
        showNotice(container, 'unknown');
        handleAtcButton(container, 'unknown');
        dispatchRestrictionEvent('unknown', null, 'zoneUpdate');
      }
      // MARK:- The zone verdict waits for nhzone:changed (the persisted cart attribute)
    });
  }

  /**
   * MARK:- Update the restriction status display
   * @param {HTMLElement} container - The container element
   * @param {string} stateCode - Two-letter US state code
   * @param {string} source - Where the state came from
   */
  function updateRestrictionStatus(container, stateCode, source) {
    const config = container._shippingConfig;

    if (!config) {
      return;
    }

    // MARK:- Determine restriction status
    const status = determineStatus(stateCode, config.restrictedStates, config.weatherStates);

    // MARK:- Update UI
    showNotice(container, status);

    // MARK:- Handle ATC button if needed
    if (config.disableAtc) {
      handleAtcButton(container, status);

      // MARK:- Update MutationObserver status if it exists
      if (container._updateObserverStatus) {
        container._updateObserverStatus(status);
      }
    }

    // MARK:- Dispatch event for other scripts
    dispatchRestrictionEvent(status, stateCode, source);
  }

  /**
   * MARK:- Determine shipping status based on state and restriction lists
   * @param {string} stateCode - Two-letter US state code
   * @param {array} restrictedStates - List of restricted state codes
   * @param {array} weatherStates - List of weather-dependent state codes
   * @returns {string} - 'unrestricted', 'restricted', 'weather', or 'unknown'
   */
  function determineStatus(stateCode, restrictedStates, weatherStates) {
    if (!stateCode) {
      return 'unknown';
    }

    const normalizedState = stateCode.toUpperCase();

    // MARK:- Check restricted first (takes priority)
    if (restrictedStates && restrictedStates.includes(normalizedState)) {
      return 'restricted';
    }

    // MARK:- Check weather-dependent
    if (weatherStates && weatherStates.includes(normalizedState)) {
      return 'weather';
    }

    // MARK:- No restrictions found
    return 'unrestricted';
  }

  /**
   * MARK:- Show the appropriate notice and hide others
   * @param {HTMLElement} container - The container element
   * @param {string} status - The status to show
   */
  function showNotice(container, status) {
    const notices = container.querySelectorAll('.shipping-restriction-notice');

    notices.forEach(notice => {
      const noticeType = notice.dataset.noticeType;
      if (noticeType === status) {
        notice.classList.remove('hide');
      } else {
        notice.classList.add('hide');
      }
    });
    // MARK:- Reveal resolved state immediately; an unresolved saved ZIP retains its bounded fallback.
    if (status !== 'unknown') {
      clearTimeout(container._locationPendingTimer);
      container.removeAttribute('data-location-pending');
    }
  }

  /**
   * MARK:- Handle Add-to-Cart button based on restriction status
   * Also handles custom third-party app buttons via configurable selectors
   * @param {HTMLElement} container - The container element
   * @param {string} status - The current status
   */
  function handleAtcButton(container, status) {
    const config = container._shippingConfig;

    // MARK:- Handle native product form submit button
    const productForm = document.querySelector('.js-product-form-main');
    if (productForm) {
      const submitButton = productForm.querySelector('button[type="submit"]');
      if (submitButton) {
        updateButtonState(submitButton, status);
      }
    }

    // MARK:- Handle custom third-party app buttons
    if (config && config.customSelectors) {
      handleCustomButtons(status, config.customSelectors);
    }

    // MARK:- Setup MutationObserver for late-loading app buttons (first time only)
    if (config && config.customSelectors && !container._observerSetup) {
      setupButtonObserver(container, status);
      container._observerSetup = true;
    }
  }

  /**
   * MARK:- Update a single button's disabled state and text
   * @param {HTMLElement} button - The button element
   * @param {string} status - The restriction status
   */
  function updateButtonState(button, status) {
    // MARK:- Store original button state if not already stored
    if (!button.dataset.originalDisabled) {
      button.dataset.originalDisabled = button.disabled ? 'true' : 'false';
      button.dataset.originalText = button.textContent;
    }

    if (status === 'restricted') {
      // MARK:- Disable button for restricted states
      button.disabled = true;
      button.textContent = 'Unavailable in Your State';
      button.classList.add('btn--disabled-restriction');
    } else {
      button.classList.remove('btn--disabled-restriction');
      // MARK:- The native picker owns current availability and the live bundle label.
      const picker = button.closest('.js-product')?.querySelector('variant-picker');
      if (button.matches('[name="add"]') && typeof picker?.updateAddToCartButton === 'function') {
        picker.updateAddToCartButton();
      } else {
        const wasOriginallyDisabled = button.dataset.originalDisabled === 'true';
        button.disabled = wasOriginallyDisabled;
        button.textContent = !wasOriginallyDisabled && button.dataset.addToCartText
          ? button.dataset.addToCartText
          : button.dataset.originalText;
      }
    }
  }

  /**
   * MARK:- Handle custom third-party app buttons via CSS selectors
   * @param {string} status - The restriction status
   * @param {string} selectors - Comma-separated CSS selectors
   */
  function handleCustomButtons(status, selectors) {
    if (!selectors) return;

    const selectorList = selectors.split(',').map(s => s.trim()).filter(s => s);

    selectorList.forEach(selector => {
      try {
        const buttons = document.querySelectorAll(selector);
        buttons.forEach(button => {
          updateButtonState(button, status);
        });
      } catch (error) {
        // Invalid selector - skip
      }
    });
  }

  /**
   * MARK:- Setup MutationObserver for late-loading app buttons
   * Third-party apps often inject their buttons after initial page load
   * @param {HTMLElement} container - The shipping restriction container
   * @param {string} initialStatus - The current restriction status
   */
  function setupButtonObserver(container, initialStatus) {
    const config = container._shippingConfig;
    if (!config || !config.customSelectors) return;

    let debounceTimeout = null;
    let currentStatus = initialStatus;

    // MARK:- Store status updater for zone changes
    container._updateObserverStatus = (newStatus) => {
      currentStatus = newStatus;
    };

    const observer = new MutationObserver(() => {
      // MARK:- Debounce to avoid excessive processing
      if (debounceTimeout) {
        clearTimeout(debounceTimeout);
      }

      debounceTimeout = setTimeout(() => {
        handleCustomButtons(currentStatus, config.customSelectors);
      }, 100);
    });

    // MARK:- Observe the document body for added nodes
    observer.observe(document.body, {
      childList: true,
      subtree: true
    });
  }

  /**
   * MARK:- Dispatch custom event for other scripts to listen to
   * @param {string} status - The restriction status
   * @param {string|null} stateCode - The detected state code
   * @param {string} source - Where the detection came from
   */
  function dispatchRestrictionEvent(status, stateCode, source) {
    const event = new CustomEvent('shipping:restriction-checked', {
      detail: {
        status: status,
        stateCode: stateCode,
        source: source,
        timestamp: new Date().toISOString()
      },
      bubbles: true
    });
    document.dispatchEvent(event);
  }

  /**
   * MARK:- Judge this product against the shopper's zone (#2899)
   * @param {HTMLElement} container - The container element
   * @returns {object|null} - NHZone.judge result, or null when NHZone is unavailable
   */
  function checkZoneProtection(container) {
    const config = container._zoneProtectionConfig;
    if (!config || !window.NHZone) return null;
    return window.NHZone.judge(config.zoneFacts, window.NHZone.currentZone());
  }

  /**
   * MARK:- Display state for a verdict: out_of_zone, in_zone, houseplant, find_zone or default
   * Patio and annual plants, and plants without zone data, keep the default Plant Sentry line.
   */
  function zoneDisplayFor(config, result) {
    if (!config.enableZoneProtection || !result) return 'default';
    if (result.verdict === 'out_of_zone' || result.verdict === 'in_zone') return result.verdict;
    if (result.verdict === 'not_judged') {
      const indoor = result.reason === 'houseplant' || /indoor|houseplant/i.test(config.zoneFacts.zoneRange);
      return indoor ? 'houseplant' : 'default';
    }
    return result.reason === 'customer_zone_unknown' ? 'find_zone' : 'default';
  }

  /**
   * MARK:- Show the one verdict line and fill its zone numbers
   * @param {HTMLElement} container - The container element
   * @param {object} result - NHZone.judge result
   */
  function updateZoneProtectionNotice(container, result) {
    const config = container._zoneProtectionConfig;
    const unrestrictedNotice = container.querySelector('[data-notice-type="unrestricted"]');
    if (!config || !unrestrictedNotice) return;

    const display = zoneDisplayFor(config, result);
    unrestrictedNotice.dataset.zoneDisplay = display;
    unrestrictedNotice.querySelectorAll('[data-zone-display-block]').forEach((block) => {
      block.classList.toggle('hide', block.dataset.zoneDisplayBlock !== display);
    });

    const zoneLabel = result && result.customerZoneNumber !== null ? String(result.customerZoneNumber) : '';
    unrestrictedNotice.querySelectorAll('[data-zone-label]').forEach((el) => { el.textContent = zoneLabel; });
    if (result && result.min !== null) {
      unrestrictedNotice.querySelectorAll('[data-zone-min]').forEach((el) => { el.textContent = result.min; });
      unrestrictedNotice.querySelectorAll('[data-zone-max]').forEach((el) => { el.textContent = result.max; });
    }
    // MARK:- The pot-and-overwinter sentence is only true on a cold-side miss (zone below the plant's minimum)
    const coldSide = Boolean(result) && window.NHZone.coldSide(result);
    unrestrictedNotice.querySelectorAll('[data-zone-cold-side]').forEach((el) => { el.classList.toggle('hide', !coldSide); });
    const shopLink = unrestrictedNotice.querySelector('[data-zone-shop-link]');
    if (shopLink && zoneLabel) {
      shopLink.href = config.zoneUrls[zoneLabel] || `/collections/all?filter.p.m.custom.growing_zones=${zoneLabel}`;
    }
  }

  /**
   * MARK:- Re-judge, update the notice and the pre-cart property field
   * @param {HTMLElement} container - The container element
   */
  function applyZoneVerdict(container) {
    const result = checkZoneProtection(container);
    if (!result) return;
    container._currentZoneStatus = result.verdict;
    container.dataset.zoneVerdict = result.verdict;
    updateZoneProtectionNotice(container, result);
    injectZonePropertyField(container);
  }

  /**
   * MARK:- Pre-cart fallback: the main form carries properties[Growing zone] only when out of zone.
   * After the add, assets/custom-cart-zone-properties.js owns the line's properties.
   * @param {HTMLElement} container - The container element
   */
  function injectZonePropertyField(container) {
    const zoneConfig = container._zoneProtectionConfig;
    if (!zoneConfig || !window.NHZone) return;

    const productForm = document.querySelector('.js-product-form-main');
    if (!productForm) return;

    const existingField = productForm.querySelector('[data-zone-property-field]');
    if (!zoneConfig.addZoneProperty || !zoneConfig.enableZoneProtection || container._currentZoneStatus !== 'out_of_zone') {
      if (existingField) existingField.remove();
      return;
    }

    const value = window.NHZone.propertyValue(window.NHZone.currentZone());
    const field = existingField || document.createElement('input');
    field.type = 'hidden';
    field.name = `properties[${window.NHZone.PROPERTY_KEY}]`;
    field.value = value;
    field.setAttribute('data-zone-property-field', 'true');
    if (!existingField) productForm.appendChild(field);
  }

  /**
   * MARK:- Safely parse JSON from data attribute
   * @param {string} jsonString - The JSON string to parse
   * @returns {array} - Parsed array or empty array on error
   */
  function parseJsonAttribute(jsonString) {
    if (!jsonString) {
      return [];
    }
    try {
      const parsed = JSON.parse(jsonString);
      return Array.isArray(parsed) ? parsed : [];
    } catch (error) {
      return [];
    }
  }

})();
